/**
 * Rehabilitation Fund API Service
 *
 * SWR hooks for fetching rehab fund data
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'
import type { RehabOverview } from '../../types/modules'

/**
 * Fetch rehabilitation fund overview for a given year
 *
 * GET /rehabilitation-fund?year={year}
 */
export function useRehabOverview(year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<RehabOverview>(
    [`/rehabilitation-fund`, { year: year.toString() }],
    apiClient.fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 5 * 60 * 1000, // 5 minutes
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch rehab fund data for specific district
 *
 * GET /rehabilitation-fund/by-district/{districtId}?year={year}
 */
export function useRehabByDistrict(districtId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<RehabOverview>(
    districtId ? [`/rehabilitation-fund/by-district/${districtId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch rehab category distribution across districts
 *
 * GET /rehabilitation-fund/by-category/{categoryId}?year={year}
 */
export function useRehabByCategory(categoryId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR(
    categoryId ? [`/rehabilitation-fund/by-category/${categoryId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch multi-year rehab fund trends
 *
 * GET /rehabilitation-fund/trends?district_id={id}&category_id={id}
 */
export function useRehabTrends(filters?: {
  districtId?: number
  categoryId?: number
}) {
  const params: Record<string, string> = {}

  if (filters?.districtId) {
    params.district_id = filters.districtId.toString()
  }
  if (filters?.categoryId) {
    params.category_id = filters.categoryId.toString()
  }

  const { data, error, isLoading, mutate } = useSWR<{
    filters: typeof params
    trends: Array<{
      year: number
      total_income: string
      total_disbursed: string
      utilization_rate: string
    }>
  }>(
    [`/rehabilitation-fund/trends`, params],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // Cache for 1 minute
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch rehab categories (reference data)
 *
 * GET /rehabilitation-categories
 */
export function useRehabCategories() {
  const { data, error, isLoading } = useSWR(
    '/rehabilitation-categories',
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 60 * 60 * 1000, // Cache for 1 hour
    }
  )

  return {
    data,
    error,
    isLoading,
  }
}
